import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'

interface ScrollVelocityProps {
  texts: ReactNode[]
  velocity?: number // base px/sec
  damping?: number // smoothing factor 0..1 (lower = faster catch-up)
  numCopies?: number // text copies per row
  velocityMultiplier?: number // how much page scroll boosts marquee
  className?: string
  scrollContainerRef?: RefObject<HTMLElement>
}

function useElementWidth<T extends HTMLElement>(ref: RefObject<T | null>): number {
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    function update() {
      if (ref.current) setWidth(ref.current.offsetWidth)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [ref])
  return width
}

function wrap(min: number, max: number, v: number): number {
  const range = max - min
  const mod = (((v - min) % range) + range) % range
  return mod + min
}

interface VelocityRowProps {
  children: ReactNode
  baseVelocity: number
  damping: number
  numCopies: number
  velocityMultiplier: number
  className?: string
  scrollContainerRef?: RefObject<HTMLElement>
}

function VelocityRow({
  children,
  baseVelocity,
  damping,
  numCopies,
  velocityMultiplier,
  className,
  scrollContainerRef,
}: VelocityRowProps) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const copyRef = useRef<HTMLSpanElement>(null)
  const copyWidth = useElementWidth(copyRef)

  // Mutable refs for animation loop (avoid re-render cost)
  const baseX = useRef(0)
  const lastScroll = useRef(0)
  const lastTime = useRef(0)
  const smoothedVel = useRef(0)
  const directionRef = useRef(1)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Static marquee: render the copies in place, no RAF loop.
      return
    }
    const target: HTMLElement | Window = scrollContainerRef?.current ?? window
    const getScrollY = () =>
      target instanceof Window ? window.scrollY : (target as HTMLElement).scrollTop

    lastScroll.current = getScrollY()
    lastTime.current = performance.now()

    const tick = (now: number) => {
      const dt = Math.max(1, now - lastTime.current) / 1000
      lastTime.current = now

      const currentScroll = getScrollY()
      const rawVel = (currentScroll - lastScroll.current) / dt
      lastScroll.current = currentScroll

      smoothedVel.current = smoothedVel.current * damping + rawVel * (1 - damping)

      if (smoothedVel.current < -1) directionRef.current = -1
      else if (smoothedVel.current > 1) directionRef.current = 1

      const velocityFactor = (smoothedVel.current * velocityMultiplier) / 1000

      let moveBy = directionRef.current * baseVelocity * dt
      moveBy += directionRef.current * moveBy * velocityFactor

      baseX.current += moveBy

      if (scrollerRef.current && copyWidth > 0) {
        const x = wrap(-copyWidth, 0, baseX.current)
        scrollerRef.current.style.transform = `translate3d(${x}px, 0, 0)`
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    const startLoop = () => {
      if (rafRef.current) return
      lastScroll.current = getScrollY()
      lastTime.current = performance.now()
      rafRef.current = requestAnimationFrame(tick)
    }
    const stopLoop = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    // Pause when marquee is offscreen — kills idle CPU + battery drain.
    const node = scrollerRef.current
    const io =
      node && typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) startLoop()
              else stopLoop()
            },
            { threshold: 0 }
          )
        : null
    if (io && node) io.observe(node)
    else startLoop()

    return () => {
      stopLoop()
      io?.disconnect()
    }
  }, [baseVelocity, damping, velocityMultiplier, copyWidth, scrollContainerRef])

  const spans = []
  for (let i = 0; i < numCopies; i++) {
    spans.push(
      <span
        key={i}
        ref={i === 0 ? copyRef : null}
        className={className}
        style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
      >
        {children}&nbsp;
      </span>
    )
  }

  const parallaxStyle: CSSProperties = {
    overflow: 'hidden',
    width: '100%',
  }
  const scrollerStyle: CSSProperties = {
    display: 'inline-flex',
    whiteSpace: 'nowrap',
    willChange: 'transform',
  }

  return (
    <div style={parallaxStyle}>
      <div ref={scrollerRef} style={scrollerStyle}>
        {spans}
      </div>
    </div>
  )
}

export default function ScrollVelocity({
  texts,
  velocity = 80,
  damping = 0.85,
  numCopies = 6,
  velocityMultiplier = 4,
  className,
  scrollContainerRef,
}: ScrollVelocityProps) {
  return (
    <div>
      {texts.map((t, i) => (
        <VelocityRow
          key={i}
          baseVelocity={i % 2 === 0 ? velocity : -velocity}
          damping={damping}
          numCopies={numCopies}
          velocityMultiplier={velocityMultiplier}
          className={className}
          scrollContainerRef={scrollContainerRef}
        >
          {t}
        </VelocityRow>
      ))}
    </div>
  )
}
